import type { CSSProperties } from 'react';

import { cn } from '@shigo-ui/ui';

import {
  DashboardDataTableStickyEdge,
  type DashboardDataTableColumn,
} from './DashboardDataTable.types';
import { DASHBOARD_TABLE_STICKY_ROW_HOVER_BG_CLASS } from './DashboardDataTable.constants';

/**
 * Returns className for sticky table cells (header or body).
 *
 * Z-index hierarchy (see DASHBOARD_TABLE_Z_INDEX):
 * - z-50: Scroll overlays (scrollbar mask, header border)
 * - z-40: Sticky column headers (sticky + pinHeader)
 * - z-20: Sticky header cells (pinHeader only)
 * - z-10: Sticky column body cells (sticky only)
 */
export function dashboardDataTableStickyCellClassName<TItem>(
  column: Pick<DashboardDataTableColumn<TItem>, 'sticky'>,
  options: { header?: boolean | undefined; pinHeader?: boolean | undefined } = {},
) {
  if (!column.sticky && !options.pinHeader) {
    return undefined;
  }

  return cn(
    'bg-card sticky',
    options.pinHeader && 'top-0',
    column.sticky && options.header && 'z-40',
    column.sticky && !options.header && 'z-10 transition-colors',
    column.sticky && !options.header && DASHBOARD_TABLE_STICKY_ROW_HOVER_BG_CLASS,
    options.pinHeader && !column.sticky && 'z-20',
  );
}

export function dashboardDataTableStickyCellStyle<TItem>(
  column: Pick<DashboardDataTableColumn<TItem>, 'sticky' | 'stickyOffset'>,
): CSSProperties | undefined {
  if (!column.sticky) {
    return undefined;
  }

  const offset = column.stickyOffset ?? '0';

  if (column.sticky === DashboardDataTableStickyEdge.Left) {
    return { left: offset };
  }

  return { right: offset };
}

export enum DashboardDataTablePinnedColumnId {
  Name = 'name',
  Select = 'select',
  Selection = 'selection',
  Selected = 'selected',
}

const pinnedColumnIds = new Set<string>(Object.values(DashboardDataTablePinnedColumnId));

export function dashboardDataTableDefaultStickyColumn<TItem>(
  column: DashboardDataTableColumn<TItem>,
): DashboardDataTableColumn<TItem> {
  if (isDashboardDataTableSelectionColumn(column.id)) {
    const sizing = 'w-10 min-w-10 max-w-10 px-0 first:pl-0 last:pr-0';
    column = {
      ...column,
      headerClassName: cn(column.headerClassName, sizing),
      className: cn(column.className, sizing),
    };
  }
  return !column.sticky && pinnedColumnIds.has(column.id)
    ? {
        ...column,
        sticky: DashboardDataTableStickyEdge.Left,
        stickyOffset: isDashboardDataTableNameColumn(column.id)
          ? 'var(--table-name-left, 0px)'
          : '0',
      }
    : column;
}

const nameColumnIds = new Set<string>([DashboardDataTablePinnedColumnId.Name]);

export function isDashboardDataTableNameColumn(id: string) {
  return nameColumnIds.has(id);
}

export function isDashboardDataTableSelectionColumn(id: string) {
  return pinnedColumnIds.has(id) && !isDashboardDataTableNameColumn(id);
}
